<template>
  <div class="instructor-quick-update">
    <h1 class="page-title">Instructor Quick Update</h1>
    
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <span>{{ loadingMessage }}</span>
    </div>
    
    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-circle"></i>
      {{ error }}
    </div>
    
    <div v-if="success" class="success-message">
      <i class="fas fa-check-circle"></i>
      {{ success }}
    </div>
    
    <div class="form-group">
      <label for="instructorId">Instructor ID</label>
      <input 
        id="instructorId" 
        v-model="instructorId" 
        type="text" 
        placeholder="Enter instructor ID"
      />
    </div>
    
    <div class="actions">
      <button @click="loadInstructor" :disabled="loading || !instructorId" class="load-btn">
        <i class="fas fa-download"></i> Load Instructor
      </button>
      
      <button @click="updateInstructor" :disabled="loading || !instructorId" class="update-btn">
        <i class="fas fa-save"></i> Update Instructor
      </button>
      
      <button @click="loadSampleData" :disabled="loading" class="sample-btn">
        <i class="fas fa-clipboard"></i> Load Sample Data
      </button>
    </div>
    
    <div v-if="instructor" class="instructor-data">
      <h3>Current Instructor Data</h3>
      <pre>{{ JSON.stringify(instructor, null, 2) }}</pre>
      
      <div class="form-group">
        <label for="updateData">Update Data (JSON)</label>
        <textarea 
          id="updateData" 
          v-model="updateData" 
          rows="10"
          placeholder="Enter JSON data to update the instructor"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { 
  getInstructorById, 
  updateInstructor as updateInstructorApi, 
  createInstructorTemplate,
  getSampleInstructorPayload
} from '@/utils/instructorService';

export default {
  name: 'InstructorQuickUpdate',
  
  setup() {
    const instructorId = ref('');
    const instructor = ref(null);
    const loading = ref(false);
    const loadingMessage = ref('');
    const error = ref('');
    const success = ref('');
    const updateData = ref('');
    
    const loadInstructor = async () => {
      if (!instructorId.value) return;
      
      loading.value = true;
      loadingMessage.value = 'Loading instructor data...';
      error.value = '';
      success.value = '';
      
      try {
        const data = await getInstructorById(instructorId.value);
        instructor.value = data;
        
        // Pre-populate the update data with the current instructor data
        // Only include the essential fields
        const template = createInstructorTemplate();
        const updatedTemplate = { ...template };
        
        // Copy over existing values from the loaded instructor
        Object.keys(template).forEach(key => {
          if (instructor.value[key] !== undefined) {
            updatedTemplate[key] = instructor.value[key];
          }
        });
        
        updateData.value = JSON.stringify(updatedTemplate, null, 2);
        
      } catch (err) {
        error.value = `Error loading instructor: ${err.message}`;
      } finally {
        loading.value = false;
      }
    };
    
    const updateInstructor = async () => {
      if (!instructorId.value) return;
      
      loading.value = true;
      loadingMessage.value = 'Updating instructor...';
      error.value = '';
      success.value = '';
      
      try {
        // Parse the JSON data
        let data = {};
        try {
          data = JSON.parse(updateData.value);
        } catch (err) {
          throw new Error(`Invalid JSON data: ${err.message}`);
        }
        
        // Send the update request
        await updateInstructorApi(instructorId.value, data);
        
        success.value = `Instructor ${instructorId.value} updated successfully`;
        
        // Reload the instructor data to show the updated values
        await loadInstructor();
        
      } catch (err) {
        error.value = `Error updating instructor: ${err.message}`;
      } finally {
        loading.value = false;
      }
    };
    
    const loadSampleData = () => {
      const sampleData = getSampleInstructorPayload();
      updateData.value = JSON.stringify(sampleData, null, 2);
    };
    
    return {
      instructorId,
      instructor,
      loading,
      loadingMessage,
      error,
      success,
      updateData,
      loadInstructor,
      updateInstructor,
      loadSampleData
    };
  }
};
</script>

<style scoped>
.instructor-quick-update {
  max-width: 800px;
  margin: 0 auto;
  padding: 1.5rem;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message,
.success-message {
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-message {
  background-color: #fee2e2;
  color: #b91c1c;
}

.success-message {
  background-color: #d1fae5;
  color: #047857;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  font-size: 1rem;
}

.actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.load-btn,
.update-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.load-btn {
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.load-btn:hover:not(:disabled) {
  background-color: #e2e8f0;
}

.update-btn {
  background-color: #3b82f6;
  border: 1px solid #3b82f6;
  color: white;
}

.update-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.load-btn:disabled,
.update-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.instructor-data {
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
}

.instructor-data h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.instructor-data pre {
  background-color: #f8fafc;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow: auto;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #1e293b;
}

.sample-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #8b5cf6;
  border: 1px solid #8b5cf6;
  color: white;
}

.sample-btn:hover:not(:disabled) {
  background-color: #7c3aed;
}

.sample-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style> 